import { useEffect, useMemo, useRef, useState } from 'react'
import {
  defaultCountries,
  FlagImage,
  parseCountry,
  usePhoneInput,
} from 'react-international-phone'
import 'react-international-phone/style.css'
import { useTranslation } from 'react-i18next'
import { getPhoneCountryFromBrowser } from '../../utils/phoneCountry'
import styles from './styles.module.css'

function normalizeSearchValue(value) {
  return value.trim().toLowerCase()
}

function matchesCountrySearch(country, query) {
  if (!query) return true

  const dialCodeQuery = query.replace(/^\+/, '')

  return (
    country.name.toLowerCase().includes(query) ||
    country.iso2.includes(query) ||
    country.dialCode.includes(dialCodeQuery)
  )
}

export function SearchablePhoneInput({
  value = '',
  onChange,
  defaultCountry,
  placeholder,
  id,
  disableCountryGuess = true,
}) {
  const { t } = useTranslation()
  const resolvedDefaultCountry = defaultCountry ?? getPhoneCountryFromBrowser()
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [search, setSearch] = useState('')
  const selectorRef = useRef(null)
  const searchInputRef = useRef(null)

  const countries = useMemo(
    () => defaultCountries.map((country) => parseCountry(country)),
    [],
  )

  const filteredCountries = useMemo(() => {
    const query = normalizeSearchValue(search)
    return countries.filter((country) => matchesCountrySearch(country, query))
  }, [countries, search])

  const {
    inputValue,
    country,
    setCountry,
    handlePhoneValueChange,
    inputRef,
  } = usePhoneInput({
    defaultCountry: resolvedDefaultCountry,
    value,
    disableCountryGuess,
    onChange: (data) => onChange?.(data.phone),
  })

  useEffect(() => {
    if (!isDropdownOpen) return undefined

    const handlePointerDown = (event) => {
      if (
        selectorRef.current &&
        !selectorRef.current.contains(event.target)
      ) {
        setIsDropdownOpen(false)
        setSearch('')
      }
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsDropdownOpen(false)
        setSearch('')
      }
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isDropdownOpen])

  useEffect(() => {
    if (!isDropdownOpen) return

    const frameId = window.requestAnimationFrame(() => {
      searchInputRef.current?.focus()
    })

    return () => window.cancelAnimationFrame(frameId)
  }, [isDropdownOpen])

  const closeDropdown = () => {
    setIsDropdownOpen(false)
    setSearch('')
  }

  const toggleDropdown = () => {
    setIsDropdownOpen((current) => {
      if (current) {
        setSearch('')
      }
      return !current
    })
  }

  const handleCountrySelect = (countryIso2) => {
    setCountry(countryIso2, { focusOnInput: true })
    closeDropdown()
  }

  return (
    <div className="react-international-phone">
      <div className="react-international-phone-input-container">
        <div
          className="react-international-phone-country-selector"
          ref={selectorRef}
        >
          <button
            type="button"
            className="react-international-phone-country-selector-button"
            onClick={toggleDropdown}
            aria-label={t('register_phone_country_search')}
            aria-haspopup="listbox"
            aria-expanded={isDropdownOpen}
          >
            <span className="react-international-phone-country-selector-button__button-content">
              <FlagImage iso2={country.iso2} className={styles.flag} />
              <span
                className={`react-international-phone-country-selector-button__dropdown-arrow ${isDropdownOpen ? 'react-international-phone-country-selector-button__dropdown-arrow--active' : ''}`}
              />
            </span>
          </button>

          {isDropdownOpen && (
            <div
              className={`react-international-phone-country-selector-dropdown ${styles.dropdown}`}
              role="listbox"
            >
              <input
                ref={searchInputRef}
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t('register_phone_country_search')}
                className={styles.searchInput}
                aria-label={t('register_phone_country_search')}
              />

              <ul className={styles.countryList}>
                {filteredCountries.map((countryOption) => {
                  const isSelected = countryOption.iso2 === country.iso2

                  return (
                    <li
                      key={countryOption.iso2}
                      role="option"
                      aria-selected={isSelected}
                      className={`react-international-phone-country-selector-dropdown__list-item ${isSelected ? 'react-international-phone-country-selector-dropdown__list-item--selected' : ''}`}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => handleCountrySelect(countryOption.iso2)}
                    >
                      <FlagImage
                        iso2={countryOption.iso2}
                        className="react-international-phone-country-selector-dropdown__list-item-flag-emoji"
                      />
                      <span className="react-international-phone-country-selector-dropdown__list-item-country-name">
                        {countryOption.name}
                      </span>
                      <span className="react-international-phone-country-selector-dropdown__list-item-dial-code">
                        +{countryOption.dialCode}
                      </span>
                    </li>
                  )
                })}
              </ul>

              {filteredCountries.length === 0 && (
                <p className={styles.emptyState}>
                  {t('register_phone_country_not_found')}
                </p>
              )}
            </div>
          )}
        </div>

        <input
          ref={inputRef}
          id={id}
          type="tel"
          value={inputValue}
          onChange={handlePhoneValueChange}
          placeholder={placeholder}
          className="react-international-phone-input"
        />
      </div>
    </div>
  )
}
